import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Accept a Travel Guardian circle invitation (email link lands here). */
export default function GuardianAccept() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Travel Guardian invitation — TravelerPulse";
    if (!token) { setErr("Missing invitation token."); return; }
    api.get("/guardian/invites/preview", { params: { token } })
      .then((r) => setPreview(r.data))
      .catch(() => setErr("This invitation was not found or has expired."));
  }, [token]);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/guardian/invites/accept", { token });
      toast.success("Invitation accepted");
      navigate("/guardian?tab=watch");
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not accept the invitation.");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-lg mx-auto px-5 py-16 text-center" data-testid="guardian-accept-page">
      <span className="inline-flex p-3 rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 text-white shadow-lg"><ShieldCheck className="w-7 h-7" /></span>
      <h1 className="mt-4 font-heading font-900 text-2xl sm:text-3xl">Travel Guardian invitation</h1>
      {err ? (
        <p className="mt-4 text-sm text-rose-600" data-testid="guardian-accept-error">{err}</p>
      ) : !preview ? (
        <p className="mt-4 text-sm text-zinc-400">Checking invitation…</p>
      ) : (
        <>
          <p className="mt-4 text-sm text-zinc-700" data-testid="guardian-accept-preview">
            <b>{preview.inviter}</b> invited you to their circle <b>"{preview.circle_name}"</b> as a <b>{preview.role.replace("_", " ")}</b> —
            so you can stay informed while they travel. Access expires {new Date(preview.access_expires_at).toLocaleDateString()}.
          </p>
          <p className="mt-2 text-xs text-zinc-500">Sharing on TravelerPulse is always voluntary and controlled by the traveler.</p>
          {user ? (
            <button onClick={accept} disabled={busy} data-testid="guardian-accept-btn"
              className="mt-6 inline-flex items-center gap-2 bg-emerald-600 text-white text-sm font-800 rounded-full px-8 py-3 hover:bg-emerald-700 disabled:opacity-50">
              <CheckCircle2 className="w-4 h-4" /> Accept invitation
            </button>
          ) : (
            <Link to={`/login?next=${encodeURIComponent(`/guardian/accept?token=${token}`)}`} data-testid="guardian-accept-signin"
              className="mt-6 inline-block bg-blue-700 text-white text-sm font-800 rounded-full px-8 py-3 hover:bg-blue-800">
              Sign in to accept
            </Link>
          )}
        </>
      )}
    </div>
  );
}
